/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import get from '@ion/core/src/environment/environment';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {Account, Asset, Schedule, User} from '@ion/core/src/types/entities';
import Features from '@/security/Features';
import {userAccountSelector} from '@/store/selectors/login-selectors';
import {addAdminUser, addUser, editAdminUser, editUser} from '../../../store/actions/admin-users';
import {addUserSelector} from '../../../store/selectors/admin-users-selectors';
import {globalGroupSelector} from '../../../store/selectors/global-group-chooser-selectors';

// defines the return type of the useUser hook...
export interface UseUserResult {
  isCurrentUser: boolean;
  globalGroup: Account;
  selected: Asset;
  loading: boolean;
  onSave: (schedule: Schedule) => void;
  onCancel: () => void;
  userAccountId: number;
  roleFilterAccountIds?: number[] | null;
};

/**
 * Defining the useUser generic hook which can be utilized by the
 * admin user editors.
 *
 * ```typescript
 *
 *  import useUser from './useUser';
 *
 *  const {
 *    globalGroup,
 *    onSave,
 *    onCancel
 *   } = useUser();
 *
 * ```
 */
export const useUser = (selector: any = addUserSelector, isEdit = false): UseUserResult => {
  const dispatch            = useDispatch();
  const globalGroup         = useSelector(globalGroupSelector) as unknown as Account;
  const userAccount         = useSelector(userAccountSelector);
  const userAccountId       = entityId(userAccount);
  const isAccountRestricted = Features.isAccountRestricted(userAccountId);

  const {
    selected,
    isCurrentUser,
    loading
  } = useSelector(selector) as any;

  const onSave = useCallback((user: User) => {
    dispatch(isEdit ? editUser(user, isCurrentUser): addUser(user) as any);
  }, [isEdit, isCurrentUser]);

  const onCancel = useCallback(() => {
    if (isEdit) {
      dispatch(editAdminUser(selected, false) as any);
    } else {
      dispatch(addAdminUser(false));
    }
  }, [isEdit, selected]);

  return {
    roleFilterAccountIds : isAccountRestricted ? null : get('restrictedAccountIds') as number[],
    userAccountId,
    isCurrentUser,
    globalGroup,
    selected,
    loading,
    onSave,
    onCancel
  };
};

export default useUser;
